import { Link } from 'react-router-dom';
import { ArrowInline } from "@/lib/Icon";
import { format, parseISO } from 'date-fns';

import { useAwaitingOrders } from '@/lib/api/adminHooks';
import type { AwaitingOrderRow } from '@/lib/api/adminHooks';

const PREVIEW_LIMIT = 3;

/**
 * Compact summary card for the Clinical Ops dashboard. Shows the count of
 * pharmacy_orders waiting for a clinician-written eRx before they can be
 * submitted to the partner pharmacy. Surfaces the auto-submit flag state
 * so ops knows whether new eRx auto-fire or require manual triggering.
 */
export function AwaitingOrdersSection() {
  const { data, isLoading, isError, error } = useAwaitingOrders();

  return (
    <section className="flex flex-col gap-12">
      <div className="flex items-baseline justify-between">
        <h2 className="type-heading-lg">Awaiting prescription</h2>
        <Link
          to="/admin/pharmacy/awaiting"
          className="type-body-sm text-accent hover:underline"
        >
          View all <ArrowInline />
        </Link>
      </div>

      {isLoading ? (
        <p className="type-body-sm text-ink-muted">Loading…</p>
      ) : isError ? (
        <p className="type-body-sm text-status-danger">
          Failed to load: {String((error as Error)?.message || error)}
        </p>
      ) : (
        <SummaryBody data={data!} />
      )}
    </section>
  );
}

function SummaryBody({
  data,
}: {
  data: {
    auto_submit_enabled: boolean;
    orders: AwaitingOrderRow[];
    total_awaiting?: number | null;
  };
}) {
  // The list holds every held order plus the newest others, not the whole queue.
  const total = data.total_awaiting ?? data.orders.length;
  const previews = data.orders.slice(0, PREVIEW_LIMIT);

  return (
    <div className="flex flex-col gap-12">
      <div className="grid grid-cols-2 gap-12">
        <KpiTile
          label="Awaiting eRx"
          value={total}
          tone={total > 0 ? 'warning' : 'muted'}
        />
        <KpiTile
          label="Auto-submit"
          value={data.auto_submit_enabled ? 'ON' : 'OFF'}
          tone={data.auto_submit_enabled ? 'success' : 'warning'}
        />
      </div>

      {!data.auto_submit_enabled && total > 0 && (
        <p className="type-body-sm text-status-warning">
          Auto-submit is OFF. New eRx will not auto-fire to the pharmacy —
          submit manually from the detail screen.
        </p>
      )}

      {previews.length === 0 ? (
        <p className="type-body-sm text-ink-muted">
          No orders awaiting prescription.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {previews.map((o) => (
            <PreviewRow key={o.id} order={o} />
          ))}
          {total > previews.length ? (
            <Link
              to="/admin/pharmacy/awaiting"
              className="type-body-sm text-accent hover:underline self-end pt-2"
            >
              + {total - previews.length} more <ArrowInline />
            </Link>
          ) : null}
        </div>
      )}
    </div>
  );
}

function KpiTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone: 'success' | 'warning' | 'danger' | 'muted';
}) {
  const toneClasses: Record<typeof tone, string> = {
    success: 'text-status-success',
    warning: 'text-status-warning',
    danger: 'text-status-danger',
    muted: 'text-ink-muted',
  };
  return (
    <div className="flex flex-col gap-2 bg-surface-subtle px-15 py-12">
      <div className="type-label-sm text-ink-muted uppercase tracking-tight">
        {label}
      </div>
      <div className={`type-heading-lg ${toneClasses[tone]}`}>{value}</div>
    </div>
  );
}

function PreviewRow({ order }: { order: AwaitingOrderRow }) {
  const when = order.created_at
    ? format(parseISO(order.created_at), 'MMM d, h:mm a')
    : '—';
  const who = order.patient_display_name || order.patient_email || order.patient_id;
  return (
    <div className="flex items-center justify-between gap-12 border border-line-subtle px-12 py-10">
      <div className="min-w-0 flex-1 flex flex-col gap-2">
        <div className="type-body-sm font-medium truncate flex items-center gap-8">
          <span className="truncate">{who}</span>
          <PartnerBadge partnerKey={order.partner_key} />
        </div>
        <div className="type-label-sm text-ink-muted truncate">
          {order.product_id ?? '—'} · {when}
        </div>
      </div>
      <Link
        to="/admin/pharmacy/awaiting"
        className="type-label-sm text-accent hover:underline shrink-0"
      >
        Review <ArrowInline />
      </Link>
    </div>
  );
}

function PartnerBadge({ partnerKey }: { partnerKey: string | null }) {
  // PPI eRx-first vs TPH PDF-first — same UX surface, different fulfilment
  // workflow downstream. Patient-facing copy doesn't change but admins
  // need to know which partner this row will hit on resubmit.
  if (!partnerKey) return null;
  const label =
    partnerKey === 'the_pharmacy_hub'
      ? 'TPH'
      : partnerKey === 'precisionmeds'
        ? 'PPI'
        : partnerKey;
  const tone =
    partnerKey === 'the_pharmacy_hub'
      ? 'bg-purple-100 text-purple-800'
      : 'bg-blue-100 text-blue-800';
  return (
    <span
      className={`shrink-0 px-6 py-1 rounded-sm type-label-sm uppercase tracking-wide ${tone}`}
    >
      {label}
    </span>
  );
}
